import { ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FeatureLockedCard } from "src/design";
import { resolveUpgradeTierFor } from "src/business/payment/upgradeTarget";
import {
  SubscriptionTier,
  useGetSubscriptionTiers,
} from "src/data/landing/queries/subscriptionTiers";
import {
  FEATURE_CAPABILITY_CONFIG,
  FeatureAccess,
  getFeatureAccess,
  isPaymentSurfaceEnabled,
} from "src/helpers/FeatureCapabilityConfig";
import { UserContext } from "src/Utils/Context/UserContext";

interface DashboardSectionProps {
  /**
   * Key into FEATURE_CAPABILITY_CONFIG, e.g. "booking_management". A list
   * unlocks the section on the union: a module sold in slices — today's service
   * with `visibilite`, the whole calendar with `reservation` — renders for
   * whichever slice the restaurant holds.
   */
  feature: string | string[];
  /** Capabilities of the restaurant currently being viewed. */
  restaurantCapabilities: string[] | undefined;
  /** The restaurant the upgrade would apply to. */
  restaurantId: string;
  /** i18n key naming the module, injected into the locked card's copy. */
  featureLabelKey: string;
  children: ReactNode;
}

/**
 * Renders a dashboard section, or the card explaining why it is unavailable:
 * an upgrade pitch when the subscription lacks the capability, a "ask your
 * administrator" note when the viewer simply lacks the permission.
 *
 * The upgrade pitch's CTA navigates rather than sends anything. It carries the
 * cheapest catalog tier that bundles the missing capability into the payment
 * declaration, so the restaurateur lands on the smallest ask that unlocks the
 * feature. With nothing in flight there is no pending or failed state left to
 * render.
 */
export const DashboardSection = ({
  feature,
  restaurantCapabilities,
  restaurantId,
  featureLabelKey,
  children,
}: DashboardSectionProps) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { data: catalog } = useGetSubscriptionTiers();

  const features = Array.isArray(feature) ? feature : [feature];
  const accesses = features.map((entry) =>
    getFeatureAccess(entry, restaurantCapabilities, user),
  );

  if (accesses.includes(FeatureAccess.Granted)) return <>{children}</>;

  // A capability the restaurant holds but the viewer was not granted is a
  // different story from one nobody here has bought: prefer the permission
  // denial, which is the one an administrator can lift without paying.
  const access = accesses.includes(FeatureAccess.MissingPermission)
    ? FeatureAccess.MissingPermission
    : FeatureAccess.MissingCapability;

  // The cheapest way back in across every slice of the module, so a locked
  // today-view sells the tier that unlocks today rather than the whole calendar.
  const upgradeTier = features
    .map((entry) => FEATURE_CAPABILITY_CONFIG[entry]?.capability)
    .filter((capability): capability is string => !!capability)
    .map((capability) => resolveUpgradeTierFor(capability, catalog))
    .reduce<SubscriptionTier | null>(
      (cheapest, tier) =>
        tier && (!cheapest || tier.monthlyPrice < cheapest.monthlyPrice)
          ? tier
          : cheapest,
      null,
    );

  // No CTA rather than one that leads to a refusal: a viewer who is not the
  // organization owner cannot declare a payment, and a capability no catalog
  // tier bundles has no upgrade to sell.
  const actionable =
    access === FeatureAccess.MissingCapability &&
    !!restaurantId &&
    !!upgradeTier &&
    isPaymentSurfaceEnabled(user);

  return (
    <FeatureLockedCard
      variant={
        access === FeatureAccess.MissingCapability ? "upgrade" : "permission"
      }
      featureLabel={t(featureLabelKey)}
      onAction={
        actionable
          ? () =>
              navigate(
                `/restaurant/${restaurantId}/payments?declare=upgrade&tier=${encodeURIComponent(
                  upgradeTier.name,
                )}`,
              )
          : null
      }
    />
  );
};
